/* eslint-disable no-console */
import React, {
  ReactElement,
  useState,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  StyledForm,
  StyledInput,
  StyledLabel,
  StyledErrorBlock,
} from './styles';
import { AlertMessage } from '../../../../components/AlertMessage/component';
import { Button } from '../../../../components/Buttons/Button/Button';
import { alertDialog } from '../../../../components/Modals/AlertDialog';
import { validatePath, createPortal } from '../../helpers';

type Inputs = {
  displayName: string;
  path: string;
  name: string;
};

type CreateInstitutionFormProps = {
  onPortalCreated: (name: string) => void;
};

export const CreateInstitutionForm = ({
  onPortalCreated,
}: CreateInstitutionFormProps): ReactElement => {
  const institutionBasePath = 'openlearning.com/institution/';
  const [saving, setSaving] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [path, setPath] = useState<string>(institutionBasePath);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    errors,
  } = useForm<Inputs>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: any): Promise<void> => {
    setSaving(true);
    const result = await createPortal(name, data.displayName);
    setSaving(false);
    if (result === true) {
      onPortalCreated(name);
    } else {
      alertDialog({
        title: 'Error',
        message: result,
      });
    }
  };

  /**
   * [Validates, sanitises and sets a url-path input provided by a user, allowing for hyphens and lowercase chars only]
   * @param  {[string]} inputPath [user input field value, expects basePath as substring prefix]
   * @param  {[string]} basePath [NOT an input, should be predefined and a substring within inputPath]
   * @param  {[boolean]} isBlur [set to true for when user unfocuses input field]
   * @return {[void]}
   */
  function handlePathChange(
    inputPath: string,
    basePath: string,
    isBlur = false
  ): void {
    // we need to list out which characters are disallowed for the user to input on the path
    // it will help validate and sanitize any input that would reach the backend
    // while providing a meaningful feedback message to the user
    // this is not an exhaustive list by any means,
    // its necessary to have two regex to handle edge cases, and possible copy-pastes
    const hyphenateRegex = / |-[^\w]|[^\w]-/g;
    const userHyphenatedPath = inputPath
      .replace(hyphenateRegex, '-')
      .toLowerCase();
    const disallowedCharsRegex = /^([^a-z0-9])|[^a-z0-9-]|--+|(-[^\w]-)/g;
    const userDirtyPath = userHyphenatedPath.slice(
      basePath.length,
      inputPath.length
    );
    const userCleanPath = userDirtyPath.replace(disallowedCharsRegex, '');
    let path = basePath + userCleanPath;
    if (isBlur && path[path.length - 1] === '-') path = path.slice(0, -1);
    setPath(path);
    setName(userCleanPath);

    validatePath(userCleanPath, (canUse: boolean) => {
      if (canUse) {
        clearErrors('name');
      } else {
        setError('name', {
          type: 'manual',
          message: 'This URL has been taken or cannot be used',
        });
      }
    });
  }

  function handleInputChange(
    val: Inputs[keyof Inputs],
    setter: Dispatch<SetStateAction<typeof val>>
  ): void {
    setter(val);
    handlePathChange(institutionBasePath + val, institutionBasePath);
  }

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <StyledLabel>Institution Name</StyledLabel>
      <StyledInput
        name="displayName"
        placeholder="Open learning university"
        value={displayName}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleInputChange(e.currentTarget.value, setDisplayName)
        }
        ref={register({ required: true })}
        required
      />

      <StyledLabel>Institution Path</StyledLabel>
      <StyledInput
        name="path"
        value={path}
        pattern="[a-z0-9-\/.]+"
        title="Only letters or numbers separated by hyphens are allowed"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handlePathChange(e.currentTarget.value, institutionBasePath)
        }
        onBlur={(e: ChangeEvent<HTMLInputElement>) =>
          handlePathChange(e.currentTarget.value, institutionBasePath, true)
        }
        ref={register({ required: true })}
        required
      />
      {errors.name && errors.name.message && (
        <StyledErrorBlock>
          <AlertMessage kind="error" message={errors.name.message} />
        </StyledErrorBlock>
      )}

      <Button
        type="submit"
        colour="cta-pink"
        disabled={saving}
        icon={saving ? 'spinner' : undefined}
        iconProps={{ spin: true }}
      >
        Create an Institution
      </Button>
    </StyledForm>
  );
};
